<template>
  <div class="messages-widget-wrapper" v-if="visible">
    <AppHeaderDropdown right no-caret class="messages-dropdown">
      <template slot="header">
        <div class="messages-widget-trigger">
          <AppIcon
            :icon="unreadCount ? 'chat-dots-fill' : 'chat-dots'"
            font-scale="1.25"
            weight="450"
          ></AppIcon>
          <span v-if="unreadCount" class="messages-widget-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
        </div>
      </template>
      <template slot="dropdown">
        <div @click.stop>

          <!-- VISTA LISTA -->
          <template v-if="view == 'list'">
            <div class="messages-panel-header">
              <span class="messages-panel-title">Mensajes</span>
              <button type="button"
                      class="messages-panel-new-btn"
                      v-b-tooltip.hover.left
                      title="Nueva conversación"
                      @click.stop.prevent="toggleStaffPicker">
                <AppIcon :icon="showStaffPicker ? 'x' : 'plus'"></AppIcon>
              </button>
            </div>

            <div class="messages-staff-picker" v-if="showStaffPicker" @click.stop>
              <div style="position:relative">
                <b-form-input ref="staffSearchInput"
                              v-model="staffSearch.query"
                              size="sm"
                              placeholder="Buscar por nombre..."
                              autocomplete="off"
                              @keyup="onStaffSearchKeyup($event)"
                              @keydown.down.prevent="staffSearchMoveDown"
                              @keydown.up.prevent="staffSearchMoveUp"
                              @keydown.enter.prevent="staffSearchConfirm"
                              @blur="staffSearchBlur"
                              @focus="staffSearchFocus">
                </b-form-input>

                <div class="messages-staff-search-results" v-if="staffSearch.show" ref="staffSearchDropdown">
                  <div v-if="staffSearch.loading" class="messages-staff-search-empty text-muted">
                    <b-spinner small class="mr-1"></b-spinner> Buscando...
                  </div>
                  <template v-else>
                    <div v-for="(item, idx) in staffSearch.results"
                         :key="item.id"
                         class="messages-staff-search-item"
                         :class="{ 'messages-staff-search-item--active': idx === staffSearch.activeIdx }"
                         @mousedown.prevent="selectStaffResult(item)">
                      <b-avatar size="1.75rem" :src="item.image" :text="avatarInitial(item.name)" variant="light" class="mr-2"></b-avatar>
                      <span>{{ item.name }}</span>
                    </div>

                    <div v-if="!staffSearch.results.length" class="messages-staff-search-empty text-muted">
                      Sin resultados para "{{ staffSearch.query }}"
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <div class="messages-panel-list">
              <div v-for="item in conversations"
                   :key="item.id"
                   class="messages-conversation-item"
                   @click="openConversation(item)">
                <b-avatar size="2.25rem" :src="item.staff_image" :text="avatarInitial(item.staff_name)" variant="light" class="messages-conversation-icon"></b-avatar>
                <div class="messages-conversation-body">
                  <div class="messages-conversation-row">
                    <span class="messages-conversation-name">{{ item.staff_name }}</span>
                    <span class="messages-conversation-time" v-if="item.last_message_at">{{ formatDate(item.last_message_at) }}</span>
                  </div>
                  <div class="messages-conversation-preview">
                    <message-body v-if="item.last_message" :body="item.last_message" plain></message-body>
                    <template v-else>Sin mensajes todavía</template>
                  </div>
                </div>
                <span v-if="item.unread_count" class="messages-conversation-unread">{{ item.unread_count > 99 ? '99+' : item.unread_count }}</span>
              </div>

              <div class="messages-panel-empty" v-if="!loadingConversations && !conversations.length">
                <AppIcon icon="chat-square-text" class="mb-1" font-scale="1.4"></AppIcon>
                <div>Todavía no tenés conversaciones</div>
              </div>
            </div>

            <router-link :to="{ name: 'MessagesStaffInbox' }" class="messages-panel-footer">
              <span>Ver todos los mensajes</span>
              <AppIcon icon="chevron-right" font-scale="0.8"></AppIcon>
            </router-link>
          </template>

          <!-- VISTA HILO -->
          <template v-else>
            <div class="messages-panel-header">
              <button type="button" class="messages-panel-back-btn" @click.stop.prevent="closeThread">
                <AppIcon icon="arrow-left"></AppIcon>
              </button>
              <span class="messages-panel-title messages-panel-title--thread">{{ activeConversation ? activeConversation.staff_name : '' }}</span>
            </div>

            <div class="messages-thread-body" ref="threadBody" @scroll="onThreadScroll">
              <div v-if="loadingMessages" class="messages-thread-loading">
                <b-spinner small class="mr-2"></b-spinner> Cargando...
              </div>
              <template v-else>
                <div v-if="loadingOlderMessages" class="messages-thread-loading-older">
                  <b-spinner small class="mr-2"></b-spinner> Cargando mensajes anteriores...
                </div>
                <template v-for="item in messagesGrouped">
                  <div v-if="item._type == 'separator'" :key="item._key" class="messages-date-separator">
                    <span>{{ formatMessageDate(item.date) }}</span>
                  </div>
                  <div v-else
                       :key="item._key"
                       class="message-bubble-row"
                       :class="isOwn(item.message) ? 'message-bubble-row--own' : 'message-bubble-row--other'">
                    <div class="message-bubble" :class="isOwn(item.message) ? 'message-bubble--own' : 'message-bubble--other'">
                      <div class="message-bubble-body"><message-body :body="item.message.body"></message-body></div>
                      <div class="message-bubble-time">{{ formatTime(item.message.created_at) }}</div>
                    </div>
                  </div>
                </template>

                <div class="messages-panel-empty" v-if="!messages.length">
                  <div>Escribí el primer mensaje</div>
                </div>
              </template>
            </div>

            <div class="messages-thread-composer">
              <div class="mention-picker" v-if="mentionPicker.active" ref="mentionDropdown">
                <div v-if="mentionPicker.loading" class="mention-picker-empty text-muted">
                  <b-spinner small class="mr-1"></b-spinner> Buscando...
                </div>
                <template v-else-if="mentionPicker.query.length < 2">
                  <div class="mention-picker-empty text-muted">Seguí escribiendo para buscar...</div>
                </template>
                <template v-else>
                  <div v-for="(item, idx) in mentionPicker.results"
                       :key="item.type + '-' + item.id"
                       class="mention-picker-item"
                       :class="{ 'mention-picker-item--active': idx === mentionPicker.activeIdx }"
                       @mousedown.prevent="insertMention(item)">
                    <AppIcon :icon="mentionIcon(item.type)" class="mention-picker-item-icon"></AppIcon>
                    <span class="mention-picker-item-label">{{ item.label }}</span>
                    <span class="mention-picker-item-type">{{ mentionLabel(item.type) }}</span>
                  </div>
                  <div v-if="!mentionPicker.results.length" class="mention-picker-empty text-muted">
                    Sin resultados para "{{ mentionPicker.query }}"
                  </div>
                </template>
              </div>
              <app-textarea ref="composerTextarea"
                                v-model="newMessageBody"
                                rows="1"
                                max-rows="3"
                                placeholder="Escribir un mensaje..."
                                :disabled="sending"
                                @keydown="onComposerKeydown"
                                @keyup="onComposerKeyup"
                                @blur="onComposerBlur">
              </app-textarea>
              <button type="button"
                      class="messages-thread-send-btn"
                      :disabled="sending || !newMessageBody || !newMessageBody.trim()"
                      @click.stop.prevent="sendMessage">
                <b-spinner small v-if="sending"></b-spinner>
                <AppIcon icon="arrow-up-circle-fill" v-else></AppIcon>
              </button>
            </div>
          </template>

        </div>
      </template>
    </AppHeaderDropdown>
  </div>
</template>

<script>
import serviceAPI from './services'
import Error from '@/handler/error'
import Session from '@/handler/session'
import { EventBus } from '@/handler/event-bus'
import Profiles from '@/config/profiles'
import moment from 'moment'
import { HeaderDropdown as AppHeaderDropdown } from '@coreui/vue'
import MessageBody from '@/components/inc/messages/MessageBody'
import { getMentionableConfig } from '@/config/mentionable'
import { buildMentionToken } from '@/handler/mentions'

const POLL_INTERVAL_MS = 30000
const THREAD_POLL_INTERVAL_MS = 7000
const STAFF_SEARCH_DEBOUNCE_MS = 250
const MENTION_SEARCH_DEBOUNCE_MS = 250

export default {
  name: 'MessagesWidget',
  components: {
    AppHeaderDropdown,
    MessageBody,
  },
  data: () => {
    return {
      visible: false,
      view: 'list',
      loadingConversations: false,
      loadingMessages: false,
      conversations: [],
      unreadCount: 0,
      activeConversation: null,
      messages: [],
      hasMoreMessages: false,
      loadingOlderMessages: false,
      newMessageBody: '',
      sending: false,
      showStaffPicker: false,
      staffSearch: {
        query: '',
        results: [],
        loading: false,
        show: false,
        activeIdx: -1,
        focusTarget: 'input',
        _timer: null,
      },
      mentionPicker: {
        active: false,
        query: '',
        results: [],
        loading: false,
        activeIdx: -1,
        atIndex: -1,
        _timer: null,
      },
      pollTimer: null,
      threadPollTimer: null,
    }
  },
  created () {
    EventBus.$on('Auth:auth', () => {
      this.setup()
    })
  },
  mounted() {
    this.setup()
    // El AppHeaderDropdown de CoreUI no re-emite los eventos de apertura y
    // cierre del b-nav-item-dropdown que arma por dentro; bootstrap-vue los
    // publica en $root, así que se escuchan ahí y se filtra que sea el nuestro.
    this.$root.$on('bv::dropdown::hidden', this.onDropdownHidden)
    this.$root.$on('bv::dropdown::shown', this.onDropdownShown)
  },
  beforeDestroy() {
    this.stopPolling()
    this.stopThreadPolling()
    clearTimeout(this.mentionPicker._timer)
    clearTimeout(this._mentionBlurTimer)
    this.$root.$off('bv::dropdown::hidden', this.onDropdownHidden)
    this.$root.$off('bv::dropdown::shown', this.onDropdownShown)
  },
  computed: {
    // Intercala separadores de fecha entre mensajes de días distintos, para
    // no repetir la fecha en cada burbuja. Se calcula una sola vez por
    // cambio de "messages" (no en cada render de cada burbuja).
    messagesGrouped() {
      var result = []
      var lastDateKey = null
      this.messages.forEach((message) => {
        var dateKey = moment(message.created_at).format('YYYY-MM-DD')
        if (dateKey !== lastDateKey) {
          result.push({ _type: 'separator', _key: 'sep-' + dateKey, date: message.created_at })
          lastDateKey = dateKey
        }
        result.push({ _type: 'message', _key: 'msg-' + message.id, message: message })
      })
      return result
    },
  },
  methods: {
    setup () {
      this.stopPolling()
      this.stopThreadPolling()

      var session = Session.getSession()

      if (session.auth && session.auth.user.profile_id == Profiles.PERSONAL && this.hasModuleAccess(session)) {
        this.visible = true
        this.view = 'list'
        this.activeConversation = null
        this.loadingConversations = true
        this.fetchUnreadCount()
        this.fetchConversations()
        this.startPolling()
      } else {
        this.visible = false
      }
    },
    hasModuleAccess(session) {
      var menu = session.auth.user.permissions && session.auth.user.permissions.menu
      if (!menu) return false

      for (const prop in menu) {
        if (menu[prop].router == 'messages') return true
      }
      return false
    },
    startPolling() {
      this.pollTimer = setInterval(() => {
        this.fetchUnreadCount()
        if (this.view == 'list') {
          this.fetchConversations()
        }
      }, POLL_INTERVAL_MS)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    startThreadPolling() {
      this.stopThreadPolling()
      this.threadPollTimer = setInterval(() => {
        this.pollNewMessages()
      }, THREAD_POLL_INTERVAL_MS)
    },
    stopThreadPolling() {
      if (this.threadPollTimer) {
        clearInterval(this.threadPollTimer)
        this.threadPollTimer = null
      }
    },
    fetchUnreadCount() {
      serviceAPI.getMessagesUnreadCount()
        .then((response) => {
          var count = response.data.count

          if (count > this.unreadCount) {
            this.playBeep()
          }

          this.unreadCount = count
        })
        .catch(() => {})
    },
    playBeep() {
      // Sonido propio (distinto al de la campana de notificaciones): dos notas
      // ascendentes en vez de un tono único, para diferenciar auditivamente
      // "mensaje nuevo" de "notificación nueva".
      try {
        var AudioContext = window.AudioContext || window.webkitAudioContext
        var ctx = new AudioContext()

        var playTone = (freq, startTime, duration) => {
          var oscillator = ctx.createOscillator()
          var gain = ctx.createGain()

          oscillator.connect(gain)
          gain.connect(ctx.destination)

          oscillator.type = 'sine'
          oscillator.frequency.value = freq
          gain.gain.setValueAtTime(0.15, startTime)
          gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration)

          oscillator.start(startTime)
          oscillator.stop(startTime + duration)
        }

        var now = ctx.currentTime
        playTone(660, now, 0.12)
        playTone(988, now + 0.1, 0.2)
      } catch (e) {
        // El navegador puede bloquear audio hasta la primera interacción del usuario
      }
    },
    fetchConversations() {
      serviceAPI.getMessagesConversations()
        .then((response) => {
          this.conversations = response.data
          this.loadingConversations = false
        })
        .catch(() => {
          this.loadingConversations = false
        })
    },
    toggleStaffPicker() {
      this.showStaffPicker = !this.showStaffPicker

      if (this.showStaffPicker) {
        this.$nextTick(() => {
          if (this.$refs.staffSearchInput) this.$refs.staffSearchInput.focus()
        })
      } else {
        this.resetStaffSearch()
      }
    },
    resetStaffSearch() {
      clearTimeout(this.staffSearch._timer)
      clearTimeout(this._staffBlurTimer)
      this.staffSearch.query = ''
      this.staffSearch.results = []
      this.staffSearch.show = false
      this.staffSearch.activeIdx = -1
      this.staffSearch.focusTarget = 'input'
    },
    onStaffSearchKeyup(event) {
      // Teclas de navegación/control: no deben disparar una búsqueda nueva
      // (si no, cada flecha resetea la selección y "refresca" el dropdown).
      var ignoredKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab', 'Shift', 'Control', 'Alt', 'Meta', 'CapsLock']
      if (event && ignoredKeys.indexOf(event.key) !== -1) return

      clearTimeout(this.staffSearch._timer)

      var q = this.staffSearch.query.trim()
      if (!q) {
        this.staffSearch.results = []
        this.staffSearch.show = false
        return
      }

      this.staffSearch._timer = setTimeout(() => this.doStaffSearch(), STAFF_SEARCH_DEBOUNCE_MS)
    },
    doStaffSearch() {
      var q = this.staffSearch.query.trim()
      if (!q) return

      this.staffSearch.loading = true
      this.staffSearch.show = true
      this.staffSearch.activeIdx = -1

      serviceAPI.filterStaffByName({ name: q })
        .then((response) => {
          this.staffSearch.loading = false
          this.staffSearch.results = response.data || []
        })
        .catch(() => {
          this.staffSearch.loading = false
          this.staffSearch.results = []
        })
    },
    staffSearchMoveDown() {
      this.staffSearch.focusTarget = 'dropdown'
      if (this.staffSearch.activeIdx < this.staffSearch.results.length - 1) {
        this.staffSearch.activeIdx++
        this.$nextTick(() => this.scrollActiveStaffResult())
      }
    },
    staffSearchMoveUp() {
      this.staffSearch.focusTarget = 'dropdown'
      if (this.staffSearch.activeIdx > -1) {
        this.staffSearch.activeIdx--
        this.$nextTick(() => this.scrollActiveStaffResult())
      }
    },
    scrollActiveStaffResult() {
      var container = this.$refs.staffSearchDropdown
      if (!container) return
      var active = container.querySelector('.messages-staff-search-item--active')
      if (active) active.scrollIntoView({ block: 'nearest' })
    },
    staffSearchConfirm() {
      if (!this.staffSearch.show || this.staffSearch.loading) {
        this.doStaffSearch()
        return
      }

      var idx = this.staffSearch.activeIdx
      if (idx >= 0 && idx < this.staffSearch.results.length) {
        this.selectStaffResult(this.staffSearch.results[idx])
      }
    },
    staffSearchBlur() {
      this._staffBlurTimer = setTimeout(() => { this.staffSearch.show = false }, 200)
    },
    staffSearchFocus() {
      this.staffSearch.focusTarget = 'input'
      if (this.staffSearch.results.length > 0) {
        this.staffSearch.show = true
      }
    },
    selectStaffResult(staff) {
      clearTimeout(this._staffBlurTimer)
      this.startConversation(staff)
    },
    startConversation(staffObject) {
      if (!staffObject) return

      serviceAPI.startMessagesConversation(staffObject.id)
        .then((response) => {
          this.showStaffPicker = false
          this.resetStaffSearch()
          this.openConversation({
            id: response.data.id,
            staff_id: response.data.staff_id,
            staff_name: response.data.staff_name,
            staff_image: response.data.staff_image,
            unread_count: 0,
          })
        })
        .catch((error) => {
          this.$awn.alert(Error.showError(error))
        })
    },
    openConversation(conversation) {
      this.activeConversation = conversation
      this.view = 'thread'
      this.showStaffPicker = false
      this.resetStaffSearch()
      this.closeMentionPicker()
      this.messages = []
      this.hasMoreMessages = false
      this.loadingOlderMessages = false
      this.loadingMessages = true
      this.fetchMessages()
      this.startThreadPolling()
    },
    closeThread() {
      this.stopThreadPolling()
      this.view = 'list'
      this.activeConversation = null
      this.closeMentionPicker()
      this.messages = []
      this.hasMoreMessages = false
      this.loadingOlderMessages = false
      this.fetchConversations()
      this.fetchUnreadCount()
    },
    // bootstrap-vue emite estos eventos para TODOS los dropdowns de la app:
    // hay que filtrar que el que se abrió/cerró sea el de este widget.
    isOwnDropdown(dropdownVm) {
      return !!(dropdownVm && dropdownVm.$el && this.$el && this.$el.contains(dropdownVm.$el))
    },
    onDropdownHidden(dropdownVm) {
      if (!this.isOwnDropdown(dropdownVm)) return
      // Cerrar el panel con un hilo abierto dejaba el polling del hilo
      // corriendo en background: cada barrido (7s) marcaba como leídos los
      // mensajes entrantes sin que nadie los viera, y el badge de no leídos
      // no se encendía nunca. Al cerrar, el hilo se cierra también.
      if (this.view == 'thread') {
        this.closeThread()
      }
    },
    onDropdownShown(dropdownVm) {
      if (!this.isOwnDropdown(dropdownVm)) return
      // Estado fresco al abrir: sin esto, la lista podía mostrar no leídos
      // viejos hasta el próximo barrido del polling general (30s).
      this.fetchConversations()
      this.fetchUnreadCount()
    },
    fetchMessages() {
      if (!this.activeConversation) return

      serviceAPI.getMessages(this.activeConversation.id)
        .then((response) => {
          this.messages = (response.data && response.data.data) || []
          this.hasMoreMessages = !!(response.data && response.data.has_more)
          this.loadingMessages = false
          this.fetchUnreadCount()
          this.$nextTick(() => this.scrollToBottom())
        })
        .catch(() => {
          this.loadingMessages = false
        })
    },
    // Polling de mensajes nuevos (7s): pide solo lo posterior al último
    // mensaje cargado y lo agrega al final, sin tocar el historial viejo
    // que el usuario haya cargado scrolleando hacia arriba.
    pollNewMessages() {
      if (!this.activeConversation) return
      if (!this.messages.length) {
        this.fetchMessages()
        return
      }

      var lastId = this.messages[this.messages.length - 1].id

      serviceAPI.getNewMessages(this.activeConversation.id, lastId)
        .then((response) => {
          var incoming = (response.data && response.data.data) || []
          if (!incoming.length) return

          var el = this.$refs.threadBody
          var wasNearBottom = !el || (el.scrollHeight - el.scrollTop - el.clientHeight < 80)

          this.messages = this.messages.concat(incoming)
          this.fetchUnreadCount()

          if (wasNearBottom) {
            this.$nextTick(() => this.scrollToBottom())
          }
        })
        .catch(() => {})
    },
    // Scroll hacia arriba: pide la página anterior al mensaje más viejo
    // cargado y la antepone, preservando la posición visual (si no, el
    // contenido nuevo empuja todo hacia abajo y el usuario pierde el lugar).
    loadOlderMessages() {
      if (!this.activeConversation || !this.hasMoreMessages || this.loadingOlderMessages || !this.messages.length) return

      this.loadingOlderMessages = true
      var oldestId = this.messages[0].id
      var el = this.$refs.threadBody
      var prevScrollHeight = el ? el.scrollHeight : 0
      var prevScrollTop = el ? el.scrollTop : 0

      serviceAPI.getMessages(this.activeConversation.id, oldestId)
        .then((response) => {
          var older = (response.data && response.data.data) || []
          this.hasMoreMessages = !!(response.data && response.data.has_more)
          this.messages = older.concat(this.messages)
          this.loadingOlderMessages = false

          this.$nextTick(() => {
            if (el) {
              el.scrollTop = prevScrollTop + (el.scrollHeight - prevScrollHeight)
            }
          })
        })
        .catch(() => {
          this.loadingOlderMessages = false
        })
    },
    onThreadScroll() {
      var el = this.$refs.threadBody
      if (!el) return
      if (el.scrollTop < 60) {
        this.loadOlderMessages()
      }
    },
    sendMessage() {
      if (!this.newMessageBody || !this.newMessageBody.trim()) return
      if (!this.activeConversation) return

      this.closeMentionPicker()
      var body = this.newMessageBody
      this.sending = true

      serviceAPI.sendMessage(this.activeConversation.id, body)
        .then((response) => {
          this.newMessageBody = ''
          this.sending = false
          this.messages.push(response.data)
          this.$nextTick(() => {
            this.scrollToBottom()
            this.focusComposer()
          })
        })
        .catch((error) => {
          this.sending = false
          this.$awn.alert(Error.showError(error))
          this.$nextTick(() => this.focusComposer())
        })
    },
    focusComposer() {
      if (this.$refs.composerTextarea) this.$refs.composerTextarea.focus()
    },
    // Maneja Enter para enviar (como antes, sin Shift/Ctrl/Alt) y, cuando el
    // picker de @ está activo, redirige flechas/Enter/Escape a navegar o
    // confirmar una mención en vez de mover el cursor o mandar el mensaje.
    onComposerKeydown(event) {
      if (this.mentionPicker.active) {
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          this.mentionMoveDown()
          return
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault()
          this.mentionMoveUp()
          return
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault()
          this.mentionConfirm()
          return
        }
        if (event.key === 'Escape') {
          event.preventDefault()
          this.closeMentionPicker()
          return
        }
      }

      if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey) {
        event.preventDefault()
        this.sendMessage()
      }
    },
    onComposerKeyup(event) {
      var ignoredKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab', 'Shift', 'Control', 'Alt', 'Meta', 'CapsLock']
      if (event && ignoredKeys.indexOf(event.key) !== -1) return
      this.detectMentionTrigger(event.target)
    },
    onComposerBlur() {
      this._mentionBlurTimer = setTimeout(() => { this.closeMentionPicker() }, 200)
    },
    // Busca el "@" más cercano hacia atrás desde el cursor: si entre ese "@"
    // y el cursor no hay espacios, es una mención en curso.
    detectMentionTrigger(el) {
      var cursor = el.selectionStart
      var value = el.value
      var textBeforeCursor = value.slice(0, cursor)
      var atIndex = textBeforeCursor.lastIndexOf('@')

      if (atIndex === -1 || /\s/.test(textBeforeCursor.slice(atIndex + 1))) {
        this.closeMentionPicker()
        return
      }

      var fragment = textBeforeCursor.slice(atIndex + 1)
      this.mentionPicker.active = true
      this.mentionPicker.atIndex = atIndex
      this.mentionPicker.query = fragment
      this.mentionPicker.activeIdx = -1

      clearTimeout(this.mentionPicker._timer)
      if (fragment.length < 2) {
        this.mentionPicker.results = []
        this.mentionPicker.loading = false
        return
      }
      this.mentionPicker._timer = setTimeout(() => this.doMentionSearch(), MENTION_SEARCH_DEBOUNCE_MS)
    },
    doMentionSearch() {
      var q = this.mentionPicker.query
      if (!q || q.length < 2) return

      this.mentionPicker.loading = true

      serviceAPI.mentionSearch(q)
        .then((response) => {
          this.mentionPicker.loading = false
          this.mentionPicker.results = response.data || []
        })
        .catch(() => {
          this.mentionPicker.loading = false
          this.mentionPicker.results = []
        })
    },
    mentionMoveDown() {
      if (this.mentionPicker.activeIdx < this.mentionPicker.results.length - 1) {
        this.mentionPicker.activeIdx++
        this.$nextTick(() => this.scrollActiveMentionResult())
      }
    },
    mentionMoveUp() {
      if (this.mentionPicker.activeIdx > -1) {
        this.mentionPicker.activeIdx--
        this.$nextTick(() => this.scrollActiveMentionResult())
      }
    },
    scrollActiveMentionResult() {
      var container = this.$refs.mentionDropdown
      if (!container) return
      var active = container.querySelector('.mention-picker-item--active')
      if (active) active.scrollIntoView({ block: 'nearest' })
    },
    mentionConfirm() {
      var idx = this.mentionPicker.activeIdx
      var item = null
      if (idx >= 0 && idx < this.mentionPicker.results.length) {
        item = this.mentionPicker.results[idx]
      } else if (this.mentionPicker.results.length === 1) {
        item = this.mentionPicker.results[0]
      }
      if (!item) return
      this.insertMention(item)
    },
    // Reemplaza desde el "@" hasta el cursor por el token de la mención
    // elegida, y deja el cursor justo después (con un espacio) para seguir
    // escribiendo sin tener que tocar el mouse.
    insertMention(item) {
      clearTimeout(this._mentionBlurTimer)
      var token = buildMentionToken(item.label, item.type, item.id)
      var value = this.newMessageBody
      var atIndex = this.mentionPicker.atIndex
      var el = this.$refs.composerTextarea ? this.$refs.composerTextarea.$refs.textarea : null
      var cursor = el ? el.selectionStart : value.length
      var before = value.slice(0, atIndex)
      var after = value.slice(cursor)
      var newValue = before + token + ' ' + after

      this.newMessageBody = newValue
      this.closeMentionPicker()

      this.$nextTick(() => {
        if (el) {
          var newCursor = (before + token + ' ').length
          el.focus()
          el.setSelectionRange(newCursor, newCursor)
        }
      })
    },
    closeMentionPicker() {
      clearTimeout(this.mentionPicker._timer)
      this.mentionPicker.active = false
      this.mentionPicker.query = ''
      this.mentionPicker.results = []
      this.mentionPicker.loading = false
      this.mentionPicker.activeIdx = -1
      this.mentionPicker.atIndex = -1
    },
    mentionIcon(type) {
      return getMentionableConfig(type).icon
    },
    mentionLabel(type) {
      return getMentionableConfig(type).label
    },
    scrollToBottom() {
      var el = this.$refs.threadBody
      if (el) el.scrollTop = el.scrollHeight
    },
    isOwn(message) {
      var currentStaffId = Session.getSession().auth.user.selector_session
      return message.staff_id == currentStaffId
    },
    avatarInitial(name) {
      return name ? name.charAt(0).toUpperCase() : '?'
    },
    formatDate(date) {
      return moment(date).fromNow()
    },
    formatTime(date) {
      return moment(date).format('HH:mm')
    },
    formatMessageDate(date) {
      var m = moment(date)
      var today = moment()
      var yesterday = moment().subtract(1, 'day')
      if (m.isSame(today, 'day')) return 'Hoy'
      if (m.isSame(yesterday, 'day')) return 'Ayer'
      if (m.isSame(today, 'year')) return m.format('D [de] MMMM')
      return m.format('D [de] MMMM [de] YYYY')
    },
  },
}
</script>

<style scoped>
.messages-widget-trigger {
  position: relative;
  width: 34px;
  height: 34px;
  margin: 0 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: var(--app-color-icon);
  transition: background-color 0.15s ease;
}
.messages-widget-trigger:hover {
  background-color: var(--app-color-primary-soft);
  color: var(--app-color-primary);
}
.messages-widget-badge {
  position: absolute;
  top: 0px;
  right: 0px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--app-color-danger);
  color: var(--app-color-on-primary);
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  box-shadow: 0 0 0 2px var(--app-color-surface);
}
</style>

<style>
.messages-dropdown .dropdown-menu {
  /* right:auto — ver comentario igual en NotificationsBell.vue: sin esto,
     left (Popper) + right:0 (.dropdown-menu-right) compiten y el panel
     puede terminar pegado al borde izquierdo según el ancho del viewport. */
  right: auto;
  min-width: 340px;
  width: 340px;
  padding: 0;
  overflow: hidden;
  background: var(--app-color-surface);
  box-shadow: var(--app-shadow-md);
}
/* Mismo conector que RemindersQuickAdd.vue/NotificationsBell.vue/
   LocationStatusIndicator.vue: el panel se alinea por su borde derecho
   contra el propio ícono, pero al ser mucho más ancho que el ícono (y con
   más burbujas a la derecha en mobile) queda visualmente desconectado.
   Offsets calculados y verificados en vivo (getBoundingClientRect) contra
   el trigger real: mismo width/margin que las otras burbujas. */
.messages-dropdown.show::after {
  content: '';
  position: absolute;
  top: 28px;
  right: 21px;
  width: 12px;
  height: 12px;
  background: var(--app-color-surface);
  transform: rotate(45deg);
  box-shadow: -1px -1px 3px rgba(11, 31, 59, 0.08);
  z-index: 1001;
  pointer-events: none;
}
.messages-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  margin: 0;
  padding: 10px 12px;
  border-bottom: 1px solid var(--app-color-border);
}
.messages-panel-title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--app-color-text);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.messages-panel-title--thread {
  text-transform: none;
  font-size: 13px;
}
.messages-panel-new-btn,
.messages-panel-back-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background-color: var(--app-color-primary-soft);
  color: var(--app-color-primary);
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.messages-panel-new-btn:hover,
.messages-panel-back-btn:hover {
  background-color: var(--app-color-primary-soft-hover);
}
.messages-staff-picker {
  padding: 10px 12px;
  border-bottom: 1px solid var(--app-color-border);
  background: var(--app-color-surface-subtle);
}
.messages-staff-search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--app-color-surface);
  border: 1px solid var(--app-color-border);
  border-top: none;
  border-radius: 0 0 8px 8px;
  box-shadow: var(--app-shadow-md);
  z-index: 200;
  max-height: 220px;
  overflow-y: auto;
}
.messages-staff-search-empty {
  padding: 0.6rem 0.85rem;
  font-size: 0.82rem;
}
.messages-staff-search-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.85rem;
  cursor: pointer;
  border-bottom: 1px solid var(--app-color-border);
  font-size: 0.85rem;
  transition: background-color 0.1s;
}
.messages-staff-search-item:last-child {
  border-bottom: none;
}
.messages-staff-search-item:hover,
.messages-staff-search-item--active {
  background: var(--app-color-primary-soft);
}
.messages-panel-list {
  max-height: 380px;
  overflow-y: auto;
}
.messages-conversation-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--app-color-border);
  cursor: pointer;
  transition: background-color 0.1s ease;
}
.messages-conversation-item:hover {
  background-color: var(--app-color-surface-subtle);
}
.messages-conversation-item:last-child {
  border-bottom: none;
}
.messages-conversation-icon {
  flex: 0 0 auto;
}
.messages-conversation-body {
  flex: 1;
  min-width: 0;
}
.messages-conversation-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px;
}
.messages-conversation-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--app-color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.messages-conversation-time {
  flex: 0 0 auto;
  font-size: 10px;
  color: var(--app-color-text-subtle);
}
.messages-conversation-preview {
  font-size: 12px;
  color: var(--app-color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 1px;
}
.messages-conversation-unread {
  flex: 0 0 auto;
  align-self: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--app-color-danger);
  color: var(--app-color-on-primary);
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
}
.messages-panel-empty {
  text-align: center;
  padding: 28px 14px;
  color: var(--app-color-text-subtle);
  font-size: 12px;
}
.messages-panel-empty .b-icon {
  color: var(--app-color-text-disabled);
}
.messages-panel-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
  color: var(--app-color-primary);
  background: var(--app-color-surface-subtle);
  border-top: 1px solid var(--app-color-border);
  text-transform: uppercase;
  transition: background-color 0.15s ease, color 0.15s ease, padding-left 0.15s ease;
}
.messages-panel-footer .b-icon {
  flex: 0 0 auto;
  transition: transform 0.15s ease;
}
.messages-panel-footer:hover,
.messages-panel-footer:focus {
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary-hover);
  padding-left: 18px;
  text-decoration: none;
}
.messages-panel-footer:hover .b-icon,
.messages-panel-footer:focus .b-icon {
  transform: translateX(3px);
}
.messages-thread-body {
  max-height: 320px;
  min-height: 200px;
  overflow-y: auto;
  padding: 10px 12px;
  background: var(--app-color-surface-subtle);
}
.messages-thread-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: var(--app-color-text-subtle);
  font-size: 12px;
}
.messages-thread-loading-older {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  color: var(--app-color-text-subtle);
  font-size: 11px;
}
.messages-date-separator {
  display: flex;
  justify-content: center;
  margin: 10px 0 8px;
}
.messages-date-separator span {
  background: var(--app-color-surface-muted);
  color: var(--app-color-text-muted);
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 10px;
  text-transform: capitalize;
}
.message-bubble-row {
  display: flex;
  margin-bottom: 6px;
}
.message-bubble-row--own {
  justify-content: flex-end;
}
.message-bubble-row--other {
  justify-content: flex-start;
}
.message-bubble {
  max-width: 78%;
  padding: 6px 10px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.35;
}
.message-bubble--own {
  background: var(--app-color-primary);
  color: var(--app-color-on-primary);
  border-bottom-right-radius: 3px;
}
.message-bubble--other {
  background: var(--app-color-surface-strong);
  color: var(--app-color-text);
  border-bottom-left-radius: 3px;
}
.message-bubble-body {
  word-break: break-word;
  white-space: pre-wrap;
}
.message-bubble-time {
  margin-top: 2px;
  font-size: 9px;
  opacity: 0.75;
  text-align: right;
}
.messages-thread-composer {
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 8px 10px;
  border-top: 1px solid var(--app-color-border);
}
.mention-picker {
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: 100%;
  margin-bottom: 6px;
  background: var(--app-color-surface);
  border: 1px solid var(--app-color-border);
  border-radius: 8px;
  box-shadow: 0 -4px 14px rgba(11, 31, 59, 0.13);
  z-index: 200;
  max-height: 220px;
  overflow-y: auto;
}
.mention-picker-empty {
  padding: 0.6rem 0.85rem;
  font-size: 0.82rem;
}
.mention-picker-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0.5rem 0.85rem;
  cursor: pointer;
  border-bottom: 1px solid var(--app-color-border);
  font-size: 0.85rem;
  transition: background-color 0.1s;
}
.mention-picker-item:last-child {
  border-bottom: none;
}
.mention-picker-item:hover,
.mention-picker-item--active {
  background: var(--app-color-primary-soft);
}
.mention-picker-item-icon {
  flex: 0 0 auto;
  opacity: 0.7;
}
.mention-picker-item-label {
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mention-picker-item-type {
  flex: 0 0 auto;
  font-size: 0.72rem;
  color: var(--app-color-text-subtle);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.messages-thread-composer textarea {
  font-size: 13px;
  resize: none;
}
.messages-thread-send-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background-color: var(--app-color-primary);
  color: var(--app-color-on-primary);
  cursor: pointer;
  transition: background-color 0.15s ease, opacity 0.15s ease;
}
.messages-thread-send-btn:disabled {
  background-color: var(--app-color-text-disabled);
  cursor: default;
}
.messages-thread-send-btn:hover:not(:disabled) {
  background-color: var(--app-color-primary-hover);
}
</style>
